import React, { useState } from 'react';
import { Button } from './ui/button';
import { MessageSquare, User, Send } from 'lucide-react';

/**
 * Reusable chronological comment thread with an appender.
 *
 * Props:
 *   entries: Array<{ id, text, timestamp, author }>  — chronological history
 *   legacyText?: string  — fallback single string (older records) shown as one entry
 *   onSubmit: async (newText) => void  — appends a new entry
 *   title: string
 *   subtitle?: string
 *   testIdPrefix?: string
 *   variant?: 'amber' | 'gray'   — amber = applicant-facing, gray = internal
 */
const CommentThread = ({
  entries = [],
  legacyText = '',
  onSubmit,
  title,
  subtitle,
  testIdPrefix = 'comment',
  variant = 'amber',
}) => {
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const palette = variant === 'gray'
    ? { bg: 'bg-gray-50', border: 'border-gray-300', head: 'text-gray-800', chip: 'bg-gray-800 text-white', hint: 'text-gray-600', focus: 'focus:ring-gray-400' }
    : { bg: 'bg-amber-50/60', border: 'border-amber-200', head: 'text-amber-900', chip: 'bg-amber-700 text-white', hint: 'text-amber-800', focus: 'focus:ring-amber-300' };

  // Build the display list: legacy string (if present + no entries) shown as one item
  const list = (entries && entries.length > 0)
    ? entries
    : (legacyText ? [{ id: 'legacy', text: legacyText, author: 'legacy', timestamp: '' }] : []);

  const submit = async () => {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    try {
      await onSubmit(text);
      setDraft('');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (ts) => {
    if (!ts) return 'earlier';
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return `${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`;
  };

  return (
    <div className={`mb-4 ${palette.bg} border ${palette.border} rounded-lg p-4`} data-testid={`${testIdPrefix}-card`}>
      <div className="flex items-center gap-2 mb-3">
        <MessageSquare className={`w-4 h-4 ${palette.head}`} />
        <h4 className={`text-sm font-semibold ${palette.head}`}>{title}</h4>
        <span className={`text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded-full ${palette.chip}`}>
          {variant === 'gray' ? 'Admin Only' : 'Applicant Visible'}
        </span>
        <span className="ml-auto text-xs text-gray-500">{list.length} {list.length === 1 ? 'entry' : 'entries'}</span>
      </div>

      {/* History list (chronological order — oldest first) */}
      <div className="space-y-2 mb-3 max-h-64 overflow-y-auto pr-1" data-testid={`${testIdPrefix}-list`}>
        {list.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No comments yet.</p>
        ) : (
          list.map((entry, idx) => (
            <div
              key={entry.id || idx}
              className="bg-white rounded-md border border-gray-100 p-3 text-sm"
              data-testid={`${testIdPrefix}-entry-${idx}`}
            >
              <p className="text-gray-800 whitespace-pre-wrap">{entry.text}</p>
              <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                <User className="w-3 h-3" />
                <span className="font-medium">{entry.author || 'admin'}</span>
                <span>·</span>
                <span>{fmt(entry.timestamp)}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Appender */}
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder={variant === 'gray'
          ? 'Add a new internal note (not shared with the family/applicant)…'
          : 'Add feedback to send to the family/applicant. They will receive an email with this new comment.'}
        rows={2}
        className={`w-full text-sm bg-white border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${palette.focus}`}
        data-testid={`${testIdPrefix}-input`}
      />
      {subtitle && <p className={`text-xs ${palette.hint} mt-1`}>{subtitle}</p>}
      <div className="flex justify-end mt-2">
        <Button
          size="sm"
          onClick={submit}
          disabled={saving || !draft.trim()}
          variant={variant === 'gray' ? 'secondary' : 'default'}
          data-testid={`${testIdPrefix}-add`}
        >
          <Send className="w-3.5 h-3.5 mr-1" />
          {saving ? 'Adding…' : (variant === 'gray' ? 'Add Internal Note' : 'Add & Notify')}
        </Button>
      </div>
    </div>
  );
};

export default CommentThread;
